import * as THREE from 'three';
import { emblemGlyphs } from '../../../content/emblems/glyphs.js';
import type { UnitEmblem } from '../../../shared/game/world-types.js';

/**
 * Gang marks as textures. A mark is drawn in code (no canvas, so it builds the same in the browser, the server's tests and a headless
 * run): the glyph's SVG path is flattened to polygons, filled with the nonzero rule into a coverage mask once, and each mark is then
 * composed from a field, a border, tally ticks and the glyph in the mark's own colours. `emblemTexture` is the chest decal (alpha outside
 * the field), `capeTexture` the same mark baked onto the cloak's cloth.
 */

/** Flattens an SVG path (M L H V C S Z, absolute or relative) into closed polygons of x, y pairs in view-box units. */
export function flattenPath(path: string, curveSteps = 8): number[][] {
  const tokens = path.match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+\.?)(?:e[-+]?\d+)?/g) ?? [];
  const polygons: number[][] = [];
  let i = 0, command = '', x = 0, y = 0, startX = 0, startY = 0, controlX = 0, controlY = 0, polygon: number[] | null = null;
  const next = () => parseFloat(tokens[i++]);
  const finish = () => { if (polygon && polygon.length >= 6) polygons.push(polygon); polygon = null; };
  const cubic = (x1: number, y1: number, x2: number, y2: number, x3: number, y3: number) => {
    for (let step = 1; step <= curveSteps; step++) {
      const t = step / curveSteps, u = 1 - t;
      polygon!.push(u * u * u * x + 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t * x3, u * u * u * y + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * y3);
    }
    controlX = x2; controlY = y2; x = x3; y = y3;
  };
  while (i < tokens.length) {
    if (/[a-zA-Z]/.test(tokens[i])) command = tokens[i++];
    else if (command === 'M') command = 'L'; else if (command === 'm') command = 'l'; // further pairs after a move are line segments
    const relative = command === command.toLowerCase(), kind = command.toLowerCase(), dx = relative ? x : 0, dy = relative ? y : 0;
    if (kind === 'z') { finish(); x = startX; y = startY; continue; }
    if (kind === 'm') { finish(); x = startX = next() + dx; y = startY = next() + dy; polygon = [x, y]; continue; }
    if (!'lhvcs'.includes(kind)) throw new Error(`Unsupported SVG path command '${command}'`);
    if (!polygon) polygon = [x, y];
    if (kind === 'l') { x = next() + dx; y = next() + dy; polygon.push(x, y); }
    else if (kind === 'h') { x = next() + dx; polygon.push(x, y); }
    else if (kind === 'v') { y = next() + dy; polygon.push(x, y); }
    else if (kind === 'c') { const x1 = next() + dx, y1 = next() + dy, x2 = next() + dx, y2 = next() + dy; cubic(x1, y1, x2, y2, next() + dx, next() + dy); }
    else { const x2 = next() + dx, y2 = next() + dy; cubic(2 * x - controlX, 2 * y - controlY, x2, y2, next() + dx, next() + dy); }
  }
  finish();
  return polygons;
}

/** The nonzero winding number of polygons about a point. */
function winding(polygons: number[][], px: number, py: number) {
  let turns = 0;
  for (const polygon of polygons) {
    for (let a = 0, n = polygon.length; a < n; a += 2) {
      const b = (a + 2) % n, x0 = polygon[a], y0 = polygon[a + 1], x1 = polygon[b], y1 = polygon[b + 1];
      const side = (x1 - x0) * (py - y0) - (px - x0) * (y1 - y0);
      if (y0 <= py) { if (y1 > py && side > 0) turns++; } else if (y1 <= py && side < 0) turns--;
    }
  }
  return turns;
}

const MASK = 128;
const masks = new Map<string, Float32Array>();
/** Coverage (0 to 1) of a glyph over a square grid, row 0 at the top of the icon. */
function glyphMask(name: string) {
  const cached = masks.get(name); if (cached) return cached;
  const glyph = emblemGlyphs[name]; if (!glyph) throw new Error(`Unknown emblem glyph '${name}'`);
  const polygons = flattenPath(glyph.path), mask = new Float32Array(MASK * MASK), scale = glyph.box / MASK, samples = 3;
  for (let row = 0; row < MASK; row++) for (let col = 0; col < MASK; col++) {
    let covered = 0;
    for (let sy = 0; sy < samples; sy++) for (let sx = 0; sx < samples; sx++) if (winding(polygons, (col + (sx + .5) / samples) * scale, (row + (sy + .5) / samples) * scale) !== 0) covered++;
    mask[row * MASK + col] = covered / (samples * samples);
  }
  masks.set(name, mask);
  return mask;
}
/** Bilinear coverage of a glyph at (u, v) in 0..1, v down the icon. */
function glyphAt(mask: Float32Array, u: number, v: number) {
  if (u < 0 || v < 0 || u > 1 || v > 1) return 0;
  const x = Math.min(MASK - 1.001, Math.max(0, u * MASK - .5)), y = Math.min(MASK - 1.001, Math.max(0, v * MASK - .5)), x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0;
  const at = (c: number, r: number) => mask[r * MASK + c];
  return (at(x0, y0) * (1 - fx) + at(x0 + 1, y0) * fx) * (1 - fy) + (at(x0, y0 + 1) * (1 - fx) + at(x0 + 1, y0 + 1) * fx) * fy;
}

/** Whether a point of the unit square (v up) lies in a field's shape, the shape shrunk about the middle by `shrink`. */
function inField(field: UnitEmblem['field'], u: number, v: number, shrink = 1) {
  const x = (u - .5) / shrink, y = (v - .5) / shrink, ax = Math.abs(x), ay = Math.abs(y);
  switch (field) {
    case 'disc': return x * x + y * y <= .48 * .48;
    case 'diamond': return ax + ay <= .5;
    case 'hex': return ax <= .43 && ay <= .5 - ax * .577;
    case 'shield': return y <= .46 && (y >= 0 ? ax <= .42 : y >= -.5 && ax <= .42 * Math.sqrt(1 - (y / -.5) ** 2));
    default: return false;
  }
}

const rgb = (color: number): [number, number, number] => [(color >> 16) & 255, (color >> 8) & 255, color & 255];
/** The mark at a point of its unit square (v up): premultiplied colour and coverage, from the layers bottom to top. */
function markAt(spec: UnitEmblem, mask: Float32Array, u: number, v: number, worn = false): [number, number, number, number] {
  let r = 0, g = 0, b = 0, a = 0;
  const lay = (color: [number, number, number], cover: number) => { if (cover <= 0) return; r = r * (1 - cover) + color[0] * cover; g = g * (1 - cover) + color[1] * cover; b = b * (1 - cover) + color[2] * cover; a = a * (1 - cover) + cover; };
  const ink = rgb(spec.ink), shaped = spec.field !== 'none';
  if (shaped) { lay(rgb(spec.fieldColor), inField(spec.field, u, v) ? 1 : 0); lay(ink, inField(spec.field, u, v) && !inField(spec.field, u, v, .91) ? 1 : 0); }
  // With no field the hood stands on its own and the bars beneath it carry the gang's colour; on a field they are ink like the hood.
  const barColor = shaped ? ink : rgb(spec.fieldColor), barY = shaped ? .2 : .09, barHalf = shaped ? .07 : .05;
  for (let tick = 0; tick < spec.ticks; tick++) lay(barColor, Math.abs(u - (.5 + (tick - (spec.ticks - 1) / 2) * .11)) < .03 && Math.abs(v - barY) < barHalf ? 1 : 0);
  const size = shaped ? .6 : spec.ticks ? .8 : .96, centre = shaped ? .5 + (spec.ticks ? .07 : 0) : spec.ticks ? .6 : .5;
  const gu = (u - .5) / size + .5, gv = 1 - ((v - centre) / size + .5);
  lay(ink, glyphAt(mask, gu, gv));
  if (worn) {
    const scuff = Math.sin(u * 31 + v * 19) * Math.sin(u * 17 - v * 37);
    const fade = .81 + scuff * .10;
    const cover = Math.sin(u * 190 + v * 13) > .98 && scuff > .4 ? .25 : 1;
    r *= fade * cover; g *= fade * cover; b *= fade * cover; a *= cover;
  }
  return [r, g, b, a];
}

const SUPER = 2;
/** The chest decal: `size` pixels square, rows from the bottom, transparent outside the mark. */
function decalImage(spec: UnitEmblem, size: number, worn: boolean) {
  const mask = glyphMask(spec.glyph), data = new Uint8Array(size * size * 4);
  for (let row = 0; row < size; row++) for (let col = 0; col < size; col++) {
    let r = 0, g = 0, b = 0, a = 0;
    for (let sy = 0; sy < SUPER; sy++) for (let sx = 0; sx < SUPER; sx++) {
      const sample = markAt(spec, mask, (col + (sx + .5) / SUPER) / size, (row + (sy + .5) / SUPER) / size, worn);
      r += sample[0]; g += sample[1]; b += sample[2]; a += sample[3]; // premultiplied, so a soft edge averages correctly
    }
    const at = (row * size + col) * 4;
    // Colour is unpremultiplied so the filtered edge keeps the mark's colours rather than fading to black.
    data[at] = a ? r / a : 0; data[at + 1] = a ? g / a : 0; data[at + 2] = a ? b / a : 0; data[at + 3] = Math.round(255 * a / (SUPER * SUPER));
  }
  return data;
}

const finish = (data: Uint8Array, width: number, height: number) => {
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  texture.colorSpace = THREE.SRGBColorSpace; texture.magFilter = THREE.LinearFilter; texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true; texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping; texture.needsUpdate = true;
  return texture;
};
const cache = new Map<string, THREE.DataTexture>();
const keyOf = (spec: UnitEmblem) => `${spec.glyph}|${spec.ink}|${spec.field}|${spec.fieldColor}|${spec.ticks}`;

/** The mark as a decal texture, shared by every unit of the gang. */
export function emblemTexture(spec: UnitEmblem, worn = false): THREE.DataTexture {
  const key = `${keyOf(spec)}|${worn}`; let texture = cache.get(key);
  if (!texture) { texture = finish(decalImage(spec, 96, worn), 96, 96); cache.set(key, texture); }
  return texture;
}

/** The cloak's cloth: its width at a distance down (as a fraction of its length) and length, and where the mark sits and how large it is, all in metres. */
export interface CapeLayout { width: (down: number) => number; length: number; side: number; drop: number }
/**
 * The mark baked onto a cloak's cloth. Rows run from the bottom of the cloak and columns from the wearer's left side seen from behind (the
 * cloak's `u` is mirrored), so the mark reads upright to someone looking at the wearer's back.
 */
export function capeTexture(spec: UnitEmblem, cloth: number, layout: CapeLayout, worn = false): THREE.DataTexture {
  const key = `${keyOf(spec)}|${cloth}|${layout.length.toFixed(3)}|${layout.side.toFixed(3)}|${layout.drop.toFixed(3)}|${layout.width(0).toFixed(3)}|${worn}`; let texture = cache.get(key);
  if (texture) return texture;
  const mask = glyphMask(spec.glyph), width = 96, height = 128, data = new Uint8Array(width * height * 4), base = rgb(cloth);
  for (let row = 0; row < height; row++) for (let col = 0; col < width; col++) {
    let r = 0, g = 0, b = 0;
    for (let sy = 0; sy < SUPER; sy++) for (let sx = 0; sx < SUPER; sx++) {
      const down = 1 - (row + (sy + .5) / SUPER) / height, across = 1 - (col + (sx + .5) / SUPER) / width; // down the cloak, then across it as geometry sees it
      const u = ((across - .5) * layout.width(down)) / layout.side + .5, v = .5 - (down * layout.length - layout.drop) / layout.side;
      const [mr, mg, mb, ma] = u < 0 || u > 1 || v < 0 || v > 1 ? [0, 0, 0, 0] : markAt(spec, mask, u, v, worn);
      r += base[0] * (1 - ma) + mr; g += base[1] * (1 - ma) + mg; b += base[2] * (1 - ma) + mb;
    }
    const at = (row * width + col) * 4, n = SUPER * SUPER;
    data[at] = r / n; data[at + 1] = g / n; data[at + 2] = b / n; data[at + 3] = 255;
  }
  texture = finish(data, width, height); cache.set(key, texture);
  return texture;
}
